import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.requires_2fa) {
        setNeeds2FA(true);
        setLoading(false);
        return;
      }
      // Admin login is admin-only — enforce role check
      const role = res.data.role;
      if (role !== 'admin' && role !== 'super_admin') {
        if (role === 'ambassador') {
          // Save customer-style token so they can access /ambassador
          localStorage.setItem('auth_token', res.data.token);
          toast.info('This is the admin login. Redirecting you to your ambassador dashboard…');
          setTimeout(() => navigate('/ambassador'), 800);
        } else {
          toast.error('This page is for admins only. Please use the customer login.');
          setTimeout(() => navigate('/login'), 1200);
        }
        setLoading(false);
        return;
      }
      localStorage.setItem('admin_token', res.data.token);
      toast.success('Welcome back, Admin');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handle2FALogin = async (e) => {
    e.preventDefault();
    if (!totpCode.trim() || totpCode.length !== 6) {
      toast.error('Enter a valid 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login-2fa`, { email, password, totp_code: totpCode });
      const role = res.data.role;
      if (role !== 'admin' && role !== 'super_admin') {
        toast.error('This page is for admins only.');
        setTimeout(() => navigate('/login'), 1200);
        setLoading(false);
        return;
      }
      localStorage.setItem('admin_token', res.data.token);
      toast.success('Welcome back, Admin');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid 2FA code');
    }
    setLoading(false);
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-4">
      <SEOHead title="Admin Sign In" noindex />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="JS Fragrance" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Admin Panel</h1>
        </div>

        {!needs2FA ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#1C7D3F] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="admin-email-input"
                required
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-base focus:border-[#1C7D3F] focus:outline-none transition-colors placeholder:text-white/30"
                data-testid="admin-password-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#1C7D3F] text-white py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#2E9B50] transition-colors disabled:opacity-50"
              data-testid="admin-login-button"
            >
              <Lock className="w-4 h-4" />
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handle2FALogin} className="space-y-4">
            <div className="text-center mb-2">
              <Shield className="w-8 h-8 text-[#1C7D3F] mx-auto mb-2" />
              <p className="text-white/60 text-sm font-sans">Enter the 6-digit code from<br />Google Authenticator</p>
            </div>
            <div>
              <input
                type="text"
                placeholder="000000"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full bg-transparent border-b border-white/20 text-white py-3 px-0 text-2xl text-center tracking-[0.5em] font-mono focus:border-[#1C7D3F] focus:outline-none transition-colors placeholder:text-white/10"
                data-testid="2fa-login-code-input"
                maxLength={6}
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#1C7D3F] text-white py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#2E9B50] transition-colors disabled:opacity-50"
              data-testid="2fa-login-button"
            >
              <Shield className="w-4 h-4" />
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <button
              type="button"
              onClick={() => { setNeeds2FA(false); setTotpCode(''); }}
              className="w-full text-white/40 text-xs font-sans hover:text-white/60 py-2"
            >
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
